import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import { api, useAuth } from "../context/AuthContext";

const SECTIONS = [
  { key: "degustazione", label: "Degustazione" },
  { key: "carta", label: "Alla Carta" },
  { key: "taglieri", label: "Taglieri" },
  { key: "dolci", label: "Dolci" },
  { key: "vini", label: "Vini" },
  { key: "carni", label: "Carni" },
];

const inputCls =
  "w-full bg-[#0D0C0A] border border-[rgba(201,162,101,0.2)] rounded-xl px-4 py-3 text-sm text-[#FAF7F2] placeholder-[#7A7267] focus:outline-none focus:border-[#C9A265] transition-colors duration-300";

const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel mb-1.5";

const deleteBtnCls =
  "h-[46px] w-[44px] rounded-xl border border-red-800/50 text-red-300 flex items-center justify-center hover:bg-red-950/50 transition-colors";

const addBtnCls =
  "flex items-center gap-2 px-5 py-3 border border-dashed border-[rgba(201,162,101,0.4)] text-[#C9A265] text-xs uppercase tracking-[0.2em] font-cinzel rounded-xl hover:bg-[#C9A265]/10 transition-colors";

const cardCls = "bg-[#171512] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 space-y-3";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const normalizeCut = (c) => ({
  ...c,
  days: typeof c.days === "string" ? { it: c.days, en: c.days } : { it: "", en: "", ...c.days },
  breed: typeof c.breed === "string" ? { it: c.breed, en: c.breed } : { it: "", en: "", ...c.breed },
  note: typeof c.note === "string" ? { it: c.note, en: c.note } : { it: "", en: "", ...c.note },
});

export default function AdminMenu() {
  const { user } = useAuth();
  const [menu, setMenu] = useState(null);
  const [carni, setCarni] = useState(null);
  const [section, setSection] = useState("degustazione");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/menu")
      .then((r) => setMenu(r.data))
      .catch(() => toast.error("Errore nel caricamento del menù"));
    api.get("/carni")
      .then((r) => setCarni((r.data || []).map(normalizeCut)))
      .catch(() => toast.error("Errore nel caricamento delle carni"));
  }, []);

  const update = (fn) =>
    setMenu((prev) => {
      const m = structuredClone(prev);
      fn(m);
      return m;
    });

  const updateCarni = (fn) =>
    setCarni((prev) => {
      const c = structuredClone(prev);
      fn(c);
      return c;
    });

  const save = async () => {
    setSaving(true);
    try {
      const cuts = structuredClone(carni);
      const used = new Set();
      cuts.forEach((c) => {
        if (!c.id || c.id.startsWith("nuovo-")) {
          const base = slugify(c.name) || "taglio";
          let id = base;
          let n = 2;
          while (used.has(id)) {
            id = `${base}-${n}`;
            n += 1;
          }
          c.id = id;
        }
        used.add(c.id);
      });
      const [menuRes, carniRes] = await Promise.all([
        api.put("/menu", menu),
        api.put("/carni", cuts),
      ]);
      setMenu(menuRes.data);
      setCarni((carniRes.data || []).map(normalizeCut));
      toast.success(<span data-testid="menu-saved-toast">Menù aggiornato — le modifiche sono già sul sito</span>);
    } catch {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const dishEditor = (dish, setDish, delDish, key, withRegion = false) => (
    <div key={key} className={cardCls}>
      {withRegion && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Regione (IT) — opzionale</label>
            <input
              data-testid={`${key}-region-it`}
              value={dish.region?.it || ""}
              onChange={(e) => setDish({ ...dish, region: { ...(dish.region || {}), it: e.target.value } })}
              className={inputCls}
              placeholder="Es. Piemonte"
            />
          </div>
          <div>
            <label className={labelCls}>Regione (EN) — opzionale</label>
            <input
              data-testid={`${key}-region-en`}
              value={dish.region?.en || ""}
              onChange={(e) => setDish({ ...dish, region: { ...(dish.region || {}), en: e.target.value } })}
              className={inputCls}
              placeholder="E.g. Piedmont"
            />
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-[1fr_1fr_110px_44px] gap-3 items-end">
        <div>
          <label className={labelCls}>Nome (IT)</label>
          <input value={dish.name.it} onChange={(e) => setDish({ ...dish, name: { ...dish.name, it: e.target.value } })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Nome (EN)</label>
          <input value={dish.name.en} onChange={(e) => setDish({ ...dish, name: { ...dish.name, en: e.target.value } })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Prezzo €</label>
          <input value={dish.price} onChange={(e) => setDish({ ...dish, price: e.target.value })} className={inputCls} />
        </div>
        <button
          data-testid={`menu-item-delete-${key}`}
          onClick={delDish}
          className={deleteBtnCls}
          title="Elimina"
        >
          <Trash2 size={16} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Descrizione (IT)</label>
          <input value={dish.desc?.it || ""} onChange={(e) => setDish({ ...dish, desc: { ...(dish.desc || {}), it: e.target.value } })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Descrizione (EN)</label>
          <input value={dish.desc?.en || ""} onChange={(e) => setDish({ ...dish, desc: { ...(dish.desc || {}), en: e.target.value } })} className={inputCls} />
        </div>
      </div>
    </div>
  );

  const newDish = () => ({ name: { it: "", en: "" }, desc: { it: "", en: "" }, price: "" });

  if (!menu || !carni) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0C0A]">
        <p className="font-cinzel tracking-[0.3em] text-xs uppercase text-[#C9A265]">Caricamento menù...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-menu-editor" className="min-h-screen bg-[#0D0C0A]">
      <header className="border-b border-[rgba(201,162,101,0.18)] bg-[#171512]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-cinzel text-sm tracking-[0.25em] text-[#FAF7F2]">MODIFICA MENÙ</p>
              <p className="text-[11px] text-[#7A7267]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/admin"
              data-testid="menu-back-to-reservations"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors"
            >
              <ArrowLeft size={14} /> Prenotazioni
            </a>
            <button
              data-testid="menu-save-button"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-[#C9A265] text-[#0D0C0A] text-xs uppercase tracking-[0.2em] font-semibold font-cinzel rounded-full hover:bg-[#E0B87A] transition-colors duration-300 disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Salvataggio..." : "Salva"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-wrap gap-2 mb-10">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              data-testid={`menu-editor-tab-${s.key}`}
              onClick={() => setSection(s.key)}
              className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] font-cinzel rounded-full border transition-all duration-300 ${
                section === s.key
                  ? "bg-[#C9A265] text-[#0D0C0A] border-[#C9A265]"
                  : "border-[rgba(201,162,101,0.3)] text-[#B0A89C] hover:border-[#C9A265] hover:text-[#C9A265]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <motion.div key={section} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          {section === "degustazione" && (
            <div className="space-y-4">
              <div className="max-w-[200px]">
                <label className={labelCls}>Prezzo degustazione (€)</label>
                <input
                  data-testid="degustazione-price-input"
                  value={menu.degustazione.price}
                  onChange={(e) => update((m) => { m.degustazione.price = e.target.value; })}
                  className={inputCls}
                />
              </div>
              {menu.degustazione.courses.map((course, i) => (
                <div key={i} className={cardCls}>
                  <div className="grid sm:grid-cols-[1fr_1fr_44px] gap-3 items-end">
                    <div>
                      <label className={labelCls}>Portata {i + 1} (IT)</label>
                      <input
                        data-testid={`degustazione-name-it-${i}`}
                        value={course.name.it}
                        onChange={(e) => update((m) => { m.degustazione.courses[i].name.it = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Portata {i + 1} (EN)</label>
                      <input
                        data-testid={`degustazione-name-en-${i}`}
                        value={course.name.en}
                        onChange={(e) => update((m) => { m.degustazione.courses[i].name.en = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <button
                      data-testid={`degustazione-delete-${i}`}
                      onClick={() => update((m) => { m.degustazione.courses.splice(i, 1); })}
                      className={deleteBtnCls}
                      title="Elimina"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Descrizione (IT)</label>
                      <input
                        data-testid={`degustazione-desc-it-${i}`}
                        value={course.desc?.it || ""}
                        onChange={(e) => update((m) => { m.degustazione.courses[i].desc = { ...(m.degustazione.courses[i].desc || {}), it: e.target.value }; })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Descrizione (EN)</label>
                      <input
                        data-testid={`degustazione-desc-en-${i}`}
                        value={course.desc?.en || ""}
                        onChange={(e) => update((m) => { m.degustazione.courses[i].desc = { ...(m.degustazione.courses[i].desc || {}), en: e.target.value }; })}
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                data-testid="degustazione-add-button"
                onClick={() => update((m) => { m.degustazione.courses.push({ name: { it: "", en: "" }, desc: { it: "", en: "" } }); })}
                className={addBtnCls}
              >
                <Plus size={14} /> Aggiungi portata
              </button>
            </div>
          )}

          {section === "carta" && (
            <div className="space-y-12">
              {menu.carta.map((group, gi) => (
                <div key={gi}>
                  <h3 className="font-serif-display text-2xl text-[#C9A265] mb-5">{group.group.it} <span className="text-sm text-[#7A7267] font-sans">/ {group.group.en}</span></h3>
                  <div className="space-y-4">
                    {group.items.map((dish, ii) =>
                      dishEditor(
                        dish,
                        (nd) => update((m) => { m.carta[gi].items[ii] = nd; }),
                        () => update((m) => { m.carta[gi].items.splice(ii, 1); }),
                        `carta-${gi}-${ii}`
                      )
                    )}
                  </div>
                  <button
                    data-testid={`carta-add-${gi}`}
                    onClick={() => update((m) => { m.carta[gi].items.push(newDish()); })}
                    className={`mt-4 ${addBtnCls}`}
                  >
                    <Plus size={14} /> Aggiungi a {group.group.it}
                  </button>
                </div>
              ))}
            </div>
          )}

          {section === "taglieri" && (
            <div className="space-y-12">
              {menu.taglieri.map((board, bi) => (
                <div key={bi} className="bg-[#171512] border border-[rgba(255,255,255,0.08)] rounded-xl p-6 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Nome tagliere (IT)</label>
                      <input value={board.name.it} onChange={(e) => update((m) => { m.taglieri[bi].name.it = e.target.value; })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Nome tagliere (EN)</label>
                      <input value={board.name.en} onChange={(e) => update((m) => { m.taglieri[bi].name.en = e.target.value; })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Prezzo (IT)</label>
                      <input value={board.price.it} onChange={(e) => update((m) => { m.taglieri[bi].price.it = e.target.value; })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Prezzo (EN)</label>
                      <input value={board.price.en} onChange={(e) => update((m) => { m.taglieri[bi].price.en = e.target.value; })} className={inputCls} />
                    </div>
                  </div>
                  {board.items.map((item, ii) => (
                    <div key={ii} className="border-t border-[rgba(255,255,255,0.06)] pt-4 space-y-3">
                      <div className="grid sm:grid-cols-[1fr_1fr_44px] gap-3 items-end">
                        <div>
                          <label className={labelCls}>Voce {ii + 1} (IT)</label>
                          <input
                            data-testid={`tagliere-${bi}-name-it-${ii}`}
                            value={item.name.it}
                            onChange={(e) => update((m) => { m.taglieri[bi].items[ii].name.it = e.target.value; })}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Voce {ii + 1} (EN)</label>
                          <input
                            data-testid={`tagliere-${bi}-name-en-${ii}`}
                            value={item.name.en}
                            onChange={(e) => update((m) => { m.taglieri[bi].items[ii].name.en = e.target.value; })}
                            className={inputCls}
                          />
                        </div>
                        <button
                          data-testid={`tagliere-${bi}-delete-${ii}`}
                          onClick={() => update((m) => { m.taglieri[bi].items.splice(ii, 1); })}
                          className={deleteBtnCls}
                          title="Elimina"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Descrizione (IT)</label>
                          <input
                            data-testid={`tagliere-${bi}-desc-it-${ii}`}
                            value={item.desc?.it || ""}
                            onChange={(e) => update((m) => { m.taglieri[bi].items[ii].desc = { ...(m.taglieri[bi].items[ii].desc || {}), it: e.target.value }; })}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className={labelCls}>Descrizione (EN)</label>
                          <input
                            data-testid={`tagliere-${bi}-desc-en-${ii}`}
                            value={item.desc?.en || ""}
                            onChange={(e) => update((m) => { m.taglieri[bi].items[ii].desc = { ...(m.taglieri[bi].items[ii].desc || {}), en: e.target.value }; })}
                            className={inputCls}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    data-testid={`tagliere-${bi}-add`}
                    onClick={() => update((m) => { m.taglieri[bi].items.push({ name: { it: "", en: "" }, desc: { it: "", en: "" } }); })}
                    className={addBtnCls}
                  >
                    <Plus size={14} /> Aggiungi voce
                  </button>
                </div>
              ))}
            </div>
          )}

          {section === "dolci" && (
            <div className="space-y-4">
              {menu.dolci.map((dish, ii) =>
                dishEditor(
                  dish,
                  (nd) => update((m) => { m.dolci[ii] = nd; }),
                  () => update((m) => { m.dolci.splice(ii, 1); }),
                  `dolci-${ii}`
                )
              )}
              <button
                data-testid="dolci-add-button"
                onClick={() => update((m) => { m.dolci.push(newDish()); })}
                className={addBtnCls}
              >
                <Plus size={14} /> Aggiungi dolce
              </button>
            </div>
          )}

          {section === "vini" && (
            <div className="space-y-12">
              {menu.vini.map((group, gi) => (
                <div key={gi}>
                  <div className="flex items-end gap-3 mb-5">
                    <div className="flex-1 grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Categoria (IT)</label>
                        <input
                          data-testid={`vini-group-it-${gi}`}
                          value={group.group.it}
                          onChange={(e) => update((m) => { m.vini[gi].group.it = e.target.value; })}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Categoria (EN)</label>
                        <input
                          data-testid={`vini-group-en-${gi}`}
                          value={group.group.en}
                          onChange={(e) => update((m) => { m.vini[gi].group.en = e.target.value; })}
                          className={inputCls}
                        />
                      </div>
                    </div>
                    <button
                      data-testid={`vini-group-delete-${gi}`}
                      onClick={() => update((m) => { m.vini.splice(gi, 1); })}
                      className={deleteBtnCls}
                      title="Elimina categoria"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="space-y-4">
                    {group.items.map((dish, ii) =>
                      dishEditor(
                        dish,
                        (nd) => update((m) => { m.vini[gi].items[ii] = nd; }),
                        () => update((m) => { m.vini[gi].items.splice(ii, 1); }),
                        `vini-${gi}-${ii}`,
                        true
                      )
                    )}
                  </div>
                  <button
                    data-testid={`vini-add-${gi}`}
                    onClick={() => update((m) => {
                      const items = m.vini[gi].items;
                      const nd = newDish();
                      const lastRegion = items.length ? items[items.length - 1].region : null;
                      if (lastRegion) nd.region = { ...lastRegion };
                      items.push(nd);
                    })}
                    className={`mt-4 ${addBtnCls}`}
                  >
                    <Plus size={14} /> Aggiungi vino
                  </button>
                </div>
              ))}
              <button
                data-testid="vini-add-group"
                onClick={() => update((m) => { m.vini.push({ group: { it: "", en: "" }, items: [] }); })}
                className={addBtnCls}
              >
                <Plus size={14} /> Aggiungi categoria vini
              </button>
            </div>
          )}

          {section === "carni" && (
            <div className="space-y-4">
              <p className="text-xs text-[#7A7267] leading-relaxed max-w-xl">
                Modifica i testi delle carni della vetrina. Dopo aver salvato, puoi cambiare la foto di ogni carne dalla pagina Gestione Foto.
              </p>
              {carni.map((cut, ci) => (
                <div key={cut.id || ci} data-testid={`carni-editor-${ci}`} className={cardCls}>
                  <div className="grid sm:grid-cols-[1fr_1fr_44px] gap-3 items-end">
                    <div>
                      <label className={labelCls}>Nome</label>
                      <input
                        data-testid={`carni-name-${ci}`}
                        value={cut.name}
                        onChange={(e) => updateCarni((c) => { c[ci].name = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Vino in abbinamento</label>
                      <input
                        data-testid={`carni-pairing-${ci}`}
                        value={cut.pairing || ""}
                        onChange={(e) => updateCarni((c) => { c[ci].pairing = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <button
                      data-testid={`carni-delete-${ci}`}
                      onClick={() => updateCarni((c) => { c.splice(ci, 1); })}
                      className={deleteBtnCls}
                      title="Elimina"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Frollatura (IT)</label>
                      <input
                        data-testid={`carni-days-it-${ci}`}
                        value={cut.days.it}
                        onChange={(e) => updateCarni((c) => { c[ci].days.it = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Frollatura (EN)</label>
                      <input
                        data-testid={`carni-days-en-${ci}`}
                        value={cut.days.en}
                        onChange={(e) => updateCarni((c) => { c[ci].days.en = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Razza (IT)</label>
                      <input
                        data-testid={`carni-breed-it-${ci}`}
                        value={cut.breed.it}
                        onChange={(e) => updateCarni((c) => { c[ci].breed.it = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Razza (EN)</label>
                      <input
                        data-testid={`carni-breed-en-${ci}`}
                        value={cut.breed.en}
                        onChange={(e) => updateCarni((c) => { c[ci].breed.en = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Descrizione (IT)</label>
                      <textarea
                        data-testid={`carni-note-it-${ci}`}
                        rows={3}
                        value={cut.note.it}
                        onChange={(e) => updateCarni((c) => { c[ci].note.it = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Descrizione (EN)</label>
                      <textarea
                        data-testid={`carni-note-en-${ci}`}
                        rows={3}
                        value={cut.note.en}
                        onChange={(e) => updateCarni((c) => { c[ci].note.en = e.target.value; })}
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>
              ))}
              <button
                data-testid="carni-add-button"
                onClick={() => updateCarni((c) => { c.push({ id: `nuovo-${Date.now().toString(36)}`, name: "", days: { it: "", en: "" }, breed: { it: "", en: "" }, note: { it: "", en: "" }, pairing: "" }); })}
                className={addBtnCls}
              >
                <Plus size={14} /> Aggiungi carne
              </button>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
